"use client";

import ChevronRight from "@/components/icons/chevron-right";
import { NavItem } from "@/lib/data";
import { cn } from "@/lib/utils";
import Link from "next/link";
import React from "react";
import { FaChevronDown } from "react-icons/fa6";

interface DesktopNavigationItemProps {
  href: string;
  displayName: string;
  nest: string;
  isPartOfNest: boolean;
  level?: number;
  subMenu?: NavItem[];
  menuOpen: string;
  setMenuOpen: (s: string) => void;
}

const DesktopNavigationItem: React.FC<DesktopNavigationItemProps> = ({
  href,
  nest,
  isPartOfNest,
  displayName,
  subMenu,
  level = 0,
  menuOpen,
  setMenuOpen,
}) => {
  const desktopSubMenu = subMenu?.filter((item) => !item.mobileOnly);
  const withSubMenu = desktopSubMenu && desktopSubMenu.length > 0;

  const openMenu = () => setMenuOpen(nest);
  const closeMenu = () => setMenuOpen("");

  const Chevron = level > 0 ? ChevronRight : FaChevronDown;

  return (
    <li className="relative cursor-pointer font-roboto">
      <Link
        href={href}
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
        prefetch={true}
        className={cn(
          "flex flex-row items-center justify-between gap-2 text-neutral-600 transition-colors hover:bg-primary-100 hover:text-primary-500",
          menuOpen.includes(nest) &&
            "!bg-primary-100 !text-body-1-400 text-primary-500",
          level > 1 && "bg-neutral-100",
          level === 1 && "bg-white",
          level > 0 &&
            "w-60 p-2 text-body-1-300 hover:!text-body-1-400 focus:!text-body-1-400",
          level === 0 && "rounded-lg p-4 text-body-1-300",
          level === 0 &&
            (isPartOfNest
              ? "bg-primary-200 text-body-1-400 text-primary-600"
              : "bg-white")
        )}
      >
        {displayName}
        {withSubMenu && (
          <Chevron
            className={cn(
              "z-10 select-none transition-transform",
              level === 0 && "size-3",
              { "-scale-x-100": menuOpen.includes(nest) && level > 0 }
            )}
          />
        )}
      </Link>
      {/* Render submenus recursively */}
      {withSubMenu && menuOpen.includes(nest) && (
        <ul
          className={cn("absolute flex shadow-md", {
            "flex-col": level === 0,
            "left-60 top-0 w-60 flex-col": level > 0,
          })}
        >
          {desktopSubMenu.map((subMenuItem) => (
            <DesktopNavigationItem
              key={subMenuItem.nest + "-" + subMenuItem.href}
              href={subMenuItem.href}
              nest={subMenuItem.nest}
              displayName={subMenuItem.displayName}
              subMenu={subMenuItem.subMenu}
              level={level + 1}
              menuOpen={menuOpen}
              isPartOfNest={false}
              setMenuOpen={setMenuOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default DesktopNavigationItem;
